import React, { PureComponent } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { branch } from "baobab-react/higher-order";
import autoBind from "react-autobind";
import ReactPlayer from "react-player";
import ReactAudioPlayer from "react-audio-player";
import DriveContext from "../../driveContext";

import { generateUUID, getFileTypeIcon, validateUrl } from "../../../../utils/utils";
import { copyToClipboard } from "../../../../utils/clipboard";
import { getUrlForExternalResource } from "../../../../actions/drive";
import { getMediaType } from "../../../../utils/mediaUtils";
import { showMediaPreviewModal } from "../../../../actions/actions";

import styles from "./tileNode.module.css";

const MAX_VISIBLE_LABELS = 6;
const DOUBLE_TAP_MS = 450;
const SYNTHETIC_CLICK_SUPPRESS_MS = 500;

function isAcceptedNodeType(type, node, accept) {
	if (type === "folder" || !accept) {
		return true;
	}

	const acceptedTypes = Array.isArray(accept) ? accept : [accept];
	const normalize = (value) => `${value || ""}`.trim().toLowerCase();
	const nodeType = normalize(node && node.metaData ? node.metaData.type : "");
	const contentType = normalize(node && (node.contentType || node.mimeType || (node.metaData && (node.metaData.contentType || node.metaData.mimeType))));
	const explicitExtension = normalize(node && (node.ext || node.extension)).replace(/^\./, "");
	const pathCandidate = normalize(node && (node.name || node.fileName || node.fullPath || (Array.isArray(node.path) ? node.path.join("/") : node.path)));
	const pathExtension = pathCandidate.includes(".") ? pathCandidate.split(".").pop() : "";
	const extension = explicitExtension || pathExtension;
	const extensionType = extension ? `extension/${extension}` : "";

	return acceptedTypes.some((acceptedTypeValue) => {
		const acceptedType = normalize(acceptedTypeValue);
		return (
		acceptedType === nodeType
		|| acceptedType === contentType
		|| acceptedType === extensionType
		|| acceptedType === extension
		|| (acceptedType === "application/json" && extension === "json")
		);
	});
}

class TileNode extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			showAllNodeLabels: false
		};
		this.lastTouchTapAt = 0;
		this.lastTouchTapKey = null;
		this.suppressClickUntil = 0;
		this.touchStart = null;
		autoBind(this);
	}

	getTapKey() {
		const { node } = this.props;
		return node.guid || node.id || node.fullPath || node.path || node.name;
	}

	handleClick(event) {
		if (Date.now() < this.suppressClickUntil) {
			return;
		}

		if (event.detail === 2) {
			this.handleDblClick();
		} else {
			this.handleSelect();
		}
	}

	handleTouchStart(event) {
		const touch = event.touches && event.touches[0];
		this.touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
	}

	handleTouchEnd(event) {
		if (this.props.node.type !== "folder") {
			return;
		}

		const touch = event.changedTouches && event.changedTouches[0];
		if (this.touchStart && touch) {
			const deltaX = Math.abs(touch.clientX - this.touchStart.x);
			const deltaY = Math.abs(touch.clientY - this.touchStart.y);
			if (deltaX > 10 || deltaY > 10) {
				this.touchStart = null;
				return;
			}
		}
		this.touchStart = null;

		event.preventDefault();
		event.stopPropagation();
		this.suppressClickUntil = Date.now() + SYNTHETIC_CLICK_SUPPRESS_MS;

		const now = Date.now();
		const tapKey = this.getTapKey();
		const isDoubleTap = this.lastTouchTapKey === tapKey && now - this.lastTouchTapAt <= DOUBLE_TAP_MS;

		this.lastTouchTapAt = isDoubleTap ? 0 : now;
		this.lastTouchTapKey = isDoubleTap ? null : tapKey;

		if (isDoubleTap) {
			this.handleDblClick();
		} else {
			this.handleSelect();
		}
	}

	handleSelect() {
		if (this.props.onSelect) {
			this.props.onSelect(this.props.node);
		}
	}

	handleToggleSelected(event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		if (this.props.selectionAvailable && this.props.onToggleSelected) {
			this.props.onToggleSelected(this.props.node);
		}
	}

	handleDblClick() {
		if (this.props.onDblClick) {
			this.props.onDblClick(this.props.node);
		}
	}

	renderHeader() {

		const { node } = this.props;
		const description = this.getNodeMetadataDescription(node);

		let icon = (node.type === "folder") ? "folder" : getFileTypeIcon(node.ext);
		let iconComponent = <FontAwesomeIcon icon={icon} />;

		return (
			<div className={styles["header-container"] + " name"} onClick={() => this.handleCopy()}>
				<div className={styles["header-main"]}>
					<div className={styles["icon-container"]}>{iconComponent}</div>
					<div className={styles["header"]} title={node.path}>{node.name}</div>
				</div>
				{description &&
					<div className={styles["description-chip"]} title={description}>
						Description
					</div>
				}
			</div>
		);
	}

	renderFilePreview() {
		const { node, iconFontSize } = this.props;
		const { guid, id, type } = node;
		const key = `node-${guid ? guid : id ? id : generateUUID()}`;
		const metadataTitle = this.getNodeMetadataTitle(node);

		if (type !== "file") {
			return null;
		}

		const url = getUrlForExternalResource(node);
		const mediaType = getMediaType(node.ext);
		let preview = null;
		switch (mediaType) {
			case "video": {
				preview = url ? <ReactPlayer url={url} style={{ width: "100%", maxWidth: "400px" }} width="100%" height="100%" playing={false} playsInline={true} muted={false} loop controls /> : null;
				break;
			}
			case "audio":
			case "labelAudioFile":
			case "label1AudioFile":
			case "label2AudioFile":
			case "commentsAudioFile": {
				preview = url ? <ReactAudioPlayer style={{ width: "100%", maxWidth: "400px" }} src={url} controls /> : null;
				break;
			}
			case "image": {
				preview = url && validateUrl(url) ? <img alt="Preview" style={{ width: "100%", maxWidth: "400px" }} src={url} /> : null;
				break;
			}
			default: {
				preview = <FontAwesomeIcon style={{ fontSize: iconFontSize ? iconFontSize : "10vw" }} icon={getFileTypeIcon(node.ext, node)} />;
			}
		}

		return <div key={key} className={styles["file-preview-container"]}>
			<div className={styles["thumb-wrapper"]}>
				<div className="thumb">
					{preview}
				</div>
				{metadataTitle &&
					<div className={styles["title-overlay"]} title={metadataTitle}>
						{metadataTitle}
					</div>
				}
			</div>
		</div>;
	}

	handleCopy = async () => {
		const url = getUrlForExternalResource(this.props.node);
		if (!url) {
			return;
		}
		await copyToClipboard(url);
	}

	normalizeLabelSource(source) {
		if (!source) {
			return [];
		}

		if (Array.isArray(source)) {
			return source
				.map((entry) => {
					if (typeof entry === "string") {
						return entry.trim();
					}
					if (entry && typeof entry === "object") {
						const value = entry.label || entry.name || entry.Name || entry.tag || entry.Tag;
						return typeof value === "string" ? value.trim() : "";
					}
					return "";
				})
				.filter(Boolean);
		}

		if (typeof source === "string") {
			return source
				.split(",")
				.map((entry) => entry.trim())
				.filter(Boolean);
		}

		if (source && typeof source === "object") {
			return Object.keys(source)
				.map((entry) => entry.trim())
				.filter(Boolean);
		}

		return [];
	}

	getNodeLabels(node) {
		const metaData = node?.metaData || {};

		const sources = [
			metaData.tags,
			node.labels,
			metaData.openAiTags,
			metaData.rekognitionTags,
			metaData.labels,
			node.tags
		];

		for (const source of sources) {
			const normalized = this.normalizeLabelSource(source);
			if (normalized.length > 0) {
				return [...new Set(normalized)];
			}
		}

		return [];
	}

	getNodeMetadataTitle(node) {
		const title = node?.metaData?.title;
		if (typeof title !== "string") {
			return "";
		}
		return title.trim();
	}

	getNodeMetadataDescription(node) {
		const description = node?.metaData?.description;
		if (typeof description !== "string") {
			return "";
		}
		return description.trim();
	}

	handleToggleLabels(event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		this.setState((prev) => ({
			showAllNodeLabels: !prev.showAllNodeLabels
		}));
	}

	renderLabels() {
		const { node, showAllLabels } = this.props;
		const { showAllNodeLabels } = this.state;
		const labels = this.getNodeLabels(node);
		if (!showAllLabels || labels.length === 0) {
			return null;
		}
		const labelClassName = node?.searchResult ? `${styles.labelTag} ${styles.labelTagSearch}` : styles.labelTag;
		const visibleLabels = showAllNodeLabels ? labels : labels.slice(0, MAX_VISIBLE_LABELS);
		const hiddenLabelCount = labels.length - visibleLabels.length;
		const canToggle = labels.length > MAX_VISIBLE_LABELS;

		return (
			<div className={styles.labels}>
				{visibleLabels.map((label, i) => (
					<span key={i} className={labelClassName}>
						{label}
					</span>
				))}
				{!showAllNodeLabels && hiddenLabelCount > 0 &&
					<button type="button" className={`${styles.labelTagMore} ${styles.labelToggleButton}`} onClick={this.handleToggleLabels}>
						+{hiddenLabelCount} more
					</button>
				}
				{showAllNodeLabels && canToggle &&
					<button type="button" className={`${styles.labelTagMore} ${styles.labelToggleButton}`} onClick={this.handleToggleLabels}>
						Show less
					</button>
				}
			</div>
		);
	}



	render() {
		const { node, meta, accept } = this.props;
		const { guid, id, type } = node;
		const { active } = meta ? meta : node;

		const key = `tile-node-${guid ? guid : id ? id : generateUUID()}`;

		// console.log("Render node", name, toggled, active);

		let fnsSelect = {};
		let acceptable = isAcceptedNodeType(type, node, accept);
		if (acceptable) {
			fnsSelect = {
				onClick: this.handleClick
			};
			if (type === "folder") {
				fnsSelect.onTouchStart = this.handleTouchStart;
				fnsSelect.onTouchEnd = this.handleTouchEnd;
			}
		}

		let previewButton;
		let mediaType = getMediaType(node.ext);
		if (mediaType === "video" ||
			mediaType === "audio" ||
			mediaType === "pdf" ||
			mediaType === "threeD" ||
			mediaType === "image") {
			let url = getUrlForExternalResource(node);

			previewButton = <div className="open-large" onClick={() => { this.props.dispatch(showMediaPreviewModal, url); }} title="View Media"><FontAwesomeIcon icon="expand-wide" /></div>;
		}

		const { selectionEnabled, selectionAvailable, isSelected } = this.props;
		const selectionControl = selectionEnabled ? (
			<label className={selectionAvailable ? styles["selection-control"] : `${styles["selection-control"]} ${styles.disabled}`} onClick={(event) => event.stopPropagation()} title={selectionAvailable ? "Select item" : "This item cannot be selected for bulk actions"}>
				<input
					type="checkbox"
					checked={!!isSelected}
					disabled={!selectionAvailable}
					onChange={this.handleToggleSelected}
				/>
			</label>
		) : null;

		const nodeClassName = acceptable
			? active
				? "tile-node selected"
				: isSelected
					? "tile-node bulk-selected"
					: "tile-node"
			: "tile-node disabled";

		return <div key={key} className={nodeClassName} style={{ touchAction: type === "folder" ? "manipulation" : undefined }} {...fnsSelect} id={`drive-node-${this.context?.driveComponentId}-${node.guid}`}>
			{selectionControl}
			{this.renderHeader()}
			{node.type === "file" && this.renderFilePreview()}
			{node.type === "file" && this.renderLabels()}
			<div className="footer">
				{previewButton}
			</div>
		</div>;
	}
}

TileNode.contextType = DriveContext;

export default branch((props) => {
	// console.log("Mapping", props.node.guid, props.node);
	if (props.node.guid) {
		return {
			meta: ["drive", "hierarchyMetadata", props.node.guid]
		};
	} else {
		return {};
	}
}, TileNode);
